import { useState, useEffect } from 'react';
import moment from 'moment';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Loader2, ExternalLink } from 'lucide-react';
import { userService } from '../../../_services';

function DeactivateReferralLinksDialog({ open, onOpenChange, userName, userId }) {
    const [linkList, setLinkList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState('');
    const [processingLinkId, setProcessingLinkId] = useState(null);

    useEffect(() => {
        if (open && userName && userId) {
            fetchUserReferralLinks();
        }
    }, [open, userName, userId]);

    const fetchUserReferralLinks = async () => {
        setLoading(true);
        setNotFound('');
        
        try {
            const apidata = await userService.Get_User_ReffralLinks(userName, userId);
            
            if (apidata && (apidata.length > 0 || apidata.length === 0)) {
                if (apidata.code !== "-1") {
                    setLinkList(apidata);
                }
                if (apidata.length === 0) {
                    setNotFound('No active link found');
                }
            }
        } catch (error) {
            console.error('Error fetching referral links:', error);
            setNotFound('Error loading links');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleLink = async (linkId, userId, isActive) => {
        setProcessingLinkId(linkId);
        
        try {
            const apidata = await userService.Update_User_ReffralLinks(linkId, userId, !isActive);
            
            if (apidata && apidata.length > 0 && apidata[0].code !== "-1") {
                // Refresh the list after a short delay
                setTimeout(() => {
                    fetchUserReferralLinks();
                }, 50);
            }
        } catch (error) {
            console.error('Error updating referral link:', error);
        } finally {
            setProcessingLinkId(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-brand-dark">
                        Disable Referral Links
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
                        </div>
                    ) : notFound ? (
                        <div className="text-center py-8 text-brand-accent">
                            {notFound}
                        </div>
                    ) : (
                        <div className="border rounded-lg overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-gray-50">
                                        <TableHead className="font-semibold">Date Created</TableHead>
                                        <TableHead className="font-semibold">Link</TableHead>
                                        <TableHead className="font-semibold text-center">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {linkList.length > 0 ? (
                                        linkList.map((link, index) => (
                                            <TableRow key={link.linkId || index}>
                                                <TableCell className="font-medium">
                                                    {moment(link.createdOn).format("YYYY-MM-DD")}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-gray-600 truncate max-w-md">
                                                            {link.baseURL}/{link.endPointText}
                                                        </span>
                                                        <a
                                                            href={`${link.baseURL}/${link.endPointText}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-brand-accent hover:text-brand-dark"
                                                        >
                                                            <ExternalLink className="h-4 w-4" />
                                                        </a>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    {link.isActive ? (
                                                        <Button
                                                            variant="destructive"
                                                            size="sm"
                                                            onClick={() => handleToggleLink(link.linkId, link.userId, link.isActive)}
                                                            disabled={processingLinkId === link.linkId}
                                                        >
                                                            {processingLinkId === link.linkId ? (
                                                                <>
                                                                    <Loader2 className="h-3 w-3 animate-spin mr-2" />
                                                                    Disabling...
                                                                </>
                                                            ) : (
                                                                'Disable'
                                                            )}
                                                        </Button>
                                                    ) : (
                                                        <span className="text-sm text-gray-400">Disabled</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                                                No referral links found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default DeactivateReferralLinksDialog;
